import { useCallback, useRef, useState } from 'react'
import { ErroRequisicao } from '../api/cliente'
import {
  useCriarEmpresa,
  useOpcoesDeEmpresa,
  useVincularEmpresa,
  type OpcaoDeEmpresa,
} from '../api/hooks-empresas'
import { useFecharAoClicarFora } from './fechar-fora'
import { Flutuante } from './Flutuante'
import { IconeBuscar, IconeEmpresa, IconeSetaBaixo } from './icones'
import { useValorComPausa } from './pausa'
import { usePerguntar } from './perguntar'
import { juntar } from './ui'

/**
 * De que empresa é este contato.
 *
 * É onde se resolve o "outro número": a pessoa escreveu de um celular novo,
 * virou contato novo, e quem atende diz de que empresa ela é. Como a empresa
 * manda na carteira, ligar pode mudar o dono do contato — e a janela diz isso
 * antes, com o nome de quem vai ficar com ele.
 *
 * Se a empresa ainda não existe, dá para cadastrar ali mesmo só com o nome;
 * CNPJ e endereço se completam depois na página Empresas. Mandar a pessoa sair
 * da conversa para cadastrar faria o vínculo ficar para depois, que é quando
 * se esquece.
 */
export function SeletorDeEmpresa({
  contato,
  podeEditar,
  podeCriarEmpresa,
}: {
  contato: {
    id: string
    nome: string
    empresaId: string | null
    empresaNome: string | null
    vendedorNome: string | null
  }
  podeEditar: boolean
  podeCriarEmpresa: boolean
}) {
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState('')
  const buscaComPausa = useValorComPausa(busca)
  const fechar = useCallback(() => {
    setAberto(false)
    setBusca('')
  }, [])
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, fechar)
  const botao = useRef<HTMLButtonElement>(null)
  const { data: opcoes, isFetching } = useOpcoesDeEmpresa(buscaComPausa.trim(), aberto)
  const vincular = useVincularEmpresa()
  const criar = useCriarEmpresa()
  const { pedirConfirmacao, dialogo } = usePerguntar()

  const falha = vincular.error ?? criar.error
  const erro = falha instanceof ErroRequisicao ? falha.corpo.mensagem : null
  const ocupado = vincular.isPending || criar.isPending

  async function ligar(empresa: OpcaoDeEmpresa) {
    fechar()
    if (empresa.id === contato.empresaId) return
    /*
      A empresa manda na carteira. Se o dono dela não é o dono do contato,
      ligar tira o contato de quem está com ele — e isso precisa ser dito
      antes, e não descoberto quando o vendedor procurar o cliente.
    */
    if (empresa.vendedorNome && empresa.vendedorNome !== contato.vendedorNome) {
      const ok = await pedirConfirmacao(
        `Ligar ${contato.nome} a ${empresa.nome}? A empresa é de ${empresa.vendedorNome}, e o contato passa para a carteira de ${empresa.vendedorNome}.`,
        { titulo: 'Ligar à empresa', rotuloConfirmar: 'Ligar', perigo: false },
      )
      if (!ok) return
    }
    vincular.mutate({ contatoId: contato.id, empresaId: empresa.id })
  }

  async function soltar() {
    fechar()
    const ok = await pedirConfirmacao(
      `Soltar ${contato.nome} de ${contato.empresaNome}? O contato continua com o mesmo dono; só deixa de aparecer na empresa.`,
      { titulo: 'Soltar da empresa', rotuloConfirmar: 'Soltar', perigo: false },
    )
    if (ok) vincular.mutate({ contatoId: contato.id, empresaId: null })
  }

  function cadastrarELigar(nome: string) {
    fechar()
    criar.mutate(
      { nome },
      { onSuccess: (nova) => vincular.mutate({ contatoId: contato.id, empresaId: nova.id }) },
    )
  }

  const termo = busca.trim()
  const lista = opcoes ?? []
  const jaExiste = lista.some((o) => o.nome.toLowerCase() === termo.toLowerCase())

  return (
    <div ref={caixa} className="relative w-full">
      <button
        ref={botao}
        type="button"
        disabled={!podeEditar || ocupado}
        onClick={() => (aberto ? fechar() : setAberto(true))}
        aria-expanded={aberto}
        className={juntar(
          'flex h-9 w-full items-center gap-2 rounded-md bg-superficie px-2.5 text-left text-sm ring-1 transition',
          aberto ? 'ring-2 ring-marca' : 'ring-tinta-200',
          podeEditar ? 'hover:ring-tinta-300' : 'cursor-default',
        )}
      >
        <IconeEmpresa className="h-4 w-4 shrink-0 text-tinta-400" />
        <span
          className={juntar(
            'min-w-0 flex-1 truncate',
            contato.empresaNome ? 'text-tinta-900' : 'text-tinta-400',
          )}
        >
          {ocupado ? 'Salvando…' : (contato.empresaNome ?? 'Sem empresa')}
        </span>
        {podeEditar ? (
          <IconeSetaBaixo
            className={juntar('h-4 w-4 shrink-0 text-tinta-400 transition', aberto && 'rotate-180')}
          />
        ) : null}
      </button>

      {erro ? <p className="mt-1 text-xs text-red-600">{erro}</p> : null}

      {/*
        Flutua fora da ficha e da janela do contato: as duas rolam por dentro
        e cortavam a lista, que abria pela metade no pé da ficha.
      */}
      <Flutuante
        ancora={botao}
        aberto={aberto}
        aoFechar={fechar}
        larguraMinima={260}
        alturaMaxima={340}
        className="flex flex-col overflow-hidden rounded-xl bg-superficie shadow-media ring-1 ring-tinta-200"
      >
        <label className="relative block border-b border-tinta-100 p-2">
          <span className="sr-only">Buscar empresa</span>
          <IconeBuscar className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-400" />
          <input
            autoFocus
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome ou CNPJ"
            className="h-8 w-full rounded-md bg-tinta-50 pl-8 pr-2 text-sm outline-none focus:bg-superficie focus:ring-2 focus:ring-marca"
          />
        </label>

        <ul className="max-h-64 min-h-0 flex-1 overflow-y-auto py-1 text-sm">
          {contato.empresaId ? (
            <li>
              <button
                type="button"
                onClick={() => void soltar()}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-tinta-50"
              >
                <span className="block h-6 w-6 shrink-0 rounded-md border border-dashed border-tinta-300" />
                <span>
                  <span className="block text-tinta-800">Soltar da empresa</span>
                  <span className="block text-xs text-tinta-400">O dono do contato não muda</span>
                </span>
              </button>
            </li>
          ) : null}

          {lista.map((empresa) => (
            <li key={empresa.id}>
              <button
                type="button"
                onClick={() => void ligar(empresa)}
                className={juntar(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-tinta-50',
                  empresa.id === contato.empresaId && 'bg-marca-clara/40',
                )}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-marca-clara text-blue-800">
                  <IconeEmpresa className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-tinta-900">{empresa.nome}</span>
                  <span className="block truncate text-xs text-tinta-400">
                    {[empresa.cnpj, empresa.vendedorNome ?? 'sem dono'].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {empresa.id === contato.empresaId ? (
                  <span className="text-sm font-semibold text-marca">✓</span>
                ) : null}
              </button>
            </li>
          ))}

          {lista.length === 0 && !isFetching ? (
            <li className="px-3 py-3 text-center text-xs text-tinta-400">
              {termo ? 'Nenhuma empresa com esse nome.' : 'Nenhuma empresa cadastrada ainda.'}
            </li>
          ) : null}

          {podeCriarEmpresa && termo && !jaExiste ? (
            <li className="border-t border-tinta-100">
              <button
                type="button"
                onClick={() => cadastrarELigar(termo)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-marca hover:bg-tinta-50"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-dashed border-marca/50 text-base leading-none">
                  +
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-medium">Cadastrar “{termo}”</span>
                  <span className="block text-xs text-tinta-400">
                    e ligar este contato; o resto se completa em Empresas
                  </span>
                </span>
              </button>
            </li>
          ) : null}
        </ul>
      </Flutuante>
      {dialogo}
    </div>
  )
}
